import {
  Component,
  OnInit,
  ViewChild,
  LOCALE_ID,
  computed,
  inject,
  signal,
  DestroyRef,
} from "@angular/core";
import { Observable } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { LayoutBreakpointService } from '@app/core/layout/layout-breakpoint.service';
import { MatDrawer } from "@angular/material/sidenav";
import { StoreAuthService } from '@app/core/auth/store-auth.service';
import { LogoutService } from '@app/core/auth/logout.service';
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import {
  DatabaseCacheService,
  ContractInfo,
  Section,
  UserData,
} from "../../../data/cache/database-cache.service";
import { Title } from "@angular/platform-browser";

import { ListSectionGroupMonthlySummariesService } from "../../../data/section-group/list-section-group-monthly-summaries.service";
import { List } from "echarts";
import { HttpParams } from "@angular/common/http";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { ThemeMode, ThemeService } from '@app/core/layout/theme.service';
import { AppHeaderComponent } from "src/app/shared/components/app-header/app-header.component";


@Component({
  selector: "system-admin-nav",
  templateUrl: "./system-admin-nav.component.html",
  styleUrls: ["./system-admin-nav.component.css"],
  imports: [...SHARED_IMPORTS, AppHeaderComponent],
})
export class SystemAdminNavComponent implements OnInit {
  // logoElement removed — logo is now rendered by AppHeaderComponent via [logoUrl] input
  @ViewChild("drawer") drawer: MatDrawer;
  
  private readonly destroyRef = inject(DestroyRef);
  private readonly themeService = inject(ThemeService);
  private readonly isEnglish = (inject(LOCALE_ID) || '').toLowerCase().startsWith('en');
  private readonly logoutService = inject(LogoutService);

  // 変数
  error: any;
  searchText: any;
  responseData: any = [];
  userPlaceHolder: any = { user: { code: "--", lastName: "", firstName: "" } };
  tempSelectedSection: Section = null;
  confirmedSelectedSection: Section = null;
  sections: Section[] = [];
  year = new Date().getFullYear();

  monthlySummaries;
  yearOfPrevMonth = null;
  monthOfPrevMonth = null;

  // contractEnabled: boolean = false;
  selectedSectionGroup: any;
  selectedSectionGroupAssets: Record<string, any>;
  selectedSectionGroupFunctions: string[];
  accountManagementRole: boolean = false;
  userManagementRole: boolean = false;

  firstSectionId;
  administeredSectionGroupId;

  public selectedSectionGroupUserDataAttributes: UserData[];
  private functionsOrder = [
    "UserData",
    "RecordList",
    "Dialog",
    "Video",
    "GOST",
  ];

  public loginUser = { loginUserName: "", isLoggedIn: false };
  loginUserJob: string = "";
  toolbarTitle: string = "";
  selectedItem = 0;
  IsNavSystemOpen: boolean = true;
  isHandset$: Observable<boolean>;
  private isHandset = false;
  readonly isSidenavCollapsed = signal(false);
  urlSelectedSectionId: string;
  readonly themeIcon = computed(() => {
    const mode = this.themeService.mode();
    if (mode === "system") return "brightness_auto";
    return mode === "dark" ? "dark_mode" : "light_mode";
  });
  readonly themeLabel = computed(() => {
    const mode = this.themeService.mode();
    if (mode === "light") return this.isEnglish ? "Switch to dark mode" : "ダークモードに切替";
    if (mode === "dark") return this.isEnglish ? "Switch to system mode" : "システム設定に切替";
    return this.isEnglish ? "Switch to light mode" : "ライトモードに切替";
  });

  private readonly layoutBreakpoint = inject(LayoutBreakpointService);

  constructor(
    private storeAuth: StoreAuthService,
    private router: Router,
    public common: DatabaseCacheService,
    private titleService: Title,
    private route: ActivatedRoute,
    private listMonthlySummaryService: ListSectionGroupMonthlySummariesService,
  ) {}

  callFullscreen() {
    if (!document.fullscreenElement) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }

  toggleTheme(): void {
    const modes: ThemeMode[] = ["light", "dark", "system"];
    const current = this.themeService.mode();
    const nextIndex = (modes.indexOf(current) + 1) % modes.length;
    this.themeService.setTheme(modes[nextIndex]);
  }

  private updateLoginUserJob(): void {
    const selectedSectionWithRole = this.common.getSelectedSectionWithRole();
    if (!selectedSectionWithRole) {
      this.loginUserJob = "";
      return;
    }
    const jobTitleCode = this.common.getSelectedSectionJobTitleCode();
    this.loginUserJob = this.common.getJobTitle(jobTitleCode);
  }

  async ngOnInit() {
    this.isHandset$ = this.layoutBreakpoint.isHandset$;

    // Track handset state for toggleSelf behavior
    this.isHandset$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isHandset) => {
      this.isHandset = isHandset;
    });

    // Auto-collapse sidenav when entering tablet mode
    this.layoutBreakpoint.isTablet$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isTablet) => {
        if (isTablet) {
          this.isSidenavCollapsed.set(true);
        }
      });

    if (this.common.getSelectedSectionGroup() == null) {
      // await this.getUseritself();
    }

    this.titleService.setTitle("Cloud System Admin");
    this.setPrevMonth();

    // user
    this.storeAuth
      .getLoginUserNameObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((name) => {
        this.loginUser.loginUserName = name || "";
      });
    this.storeAuth
      .getIsLoggedInOservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isLoggedIn) => {
        this.loginUser.isLoggedIn = isLoggedIn;
      });
    this.updateLoginUserJob();

    // this.contractEnabled = true;

    this.common.parametersUpdated
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((noData) => {
      this.setPrevMonth();
      this.updateLoginUserJob();
    });

    this.route.firstChild.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
      this.setPrevMonth();

      let sectionWithRole = this.common.getSectionWithRole();
      if (sectionWithRole != null && sectionWithRole.length > 0) {
        this.firstSectionId = sectionWithRole[0].sectionId;
      }

      if (this.common.hasAllSectionGroupManagement()) {
        this.administeredSectionGroupId = this.common.getSectionGroupDocuments()?.[0]?.id ?? null;
      } 
      else
      {
        let sectionGroups = this.common.getSectionGroupAdmins();
        if (sectionGroups != null && sectionGroups.length > 0) {
          this.administeredSectionGroupId = sectionGroups[0].sectionGroupId;
        }
      }

      let selectedSectionGroup = this.common.getSelectedSectionGroup();
      if (selectedSectionGroup == null || selectedSectionGroup == undefined) {
        // let sectionGroups = this.common.getSectionGroupDocuments();
        if (this.administeredSectionGroupId != null) {
          this.common.forceSetSelectedSectionGroup(this.administeredSectionGroupId);
        }
      }
    });
  }

  logout() {
    this.logoutService.logout();
  }

  onSelectionChange(event: any) {
  }

  setPrevMonth() {
    this.yearOfPrevMonth = new Date().getFullYear();
    this.monthOfPrevMonth = new Date().getMonth();

    if (this.monthOfPrevMonth == 0) {
      this.monthOfPrevMonth = 12;
      this.yearOfPrevMonth--;
    }
  }

  toggleSelf() {
    if (this.isHandset) {
      // Mobile: toggle drawer (overlay mode only)
      this.drawer?.toggle();
    } else {
      // Tablet/Desktop: toggle collapsed state
      this.isSidenavCollapsed.update(v => !v);
    }
  }

  public sectionComparison = function (option, value): boolean {
    return option.sectionId === value.sectionId;
  };

  openNewWindow(ruterurl) {
    // Converts the route into a string that can be used
    // with the window.open() function
    const url = this.router.serializeUrl(this.router.createUrlTree([ruterurl]));
    window.open("#" + url, "_blank");
  }

  async getUseritself(): Promise<void> {
    try {
      this.urlSelectedSectionId = this.route.snapshot.paramMap.get("sectionId"); // sectionId in the URL pass
      const responseData = await this.common.setLoginUserAndSelectedSection(
        this.urlSelectedSectionId,
      );
    } catch (error) {
    }
  }
}
